import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { TerraDraw, TerraDrawRectangleMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { MODES } from './TerraDrawControls';

const RECTANGLE_STYLES = {
    fillColor: '#0069D2',
    fillOpacity: 0.3,
    outlineColor: '#0069D2',
    strokeColor: '#0069D2',
    strokeOpacity: 1,
    strokeWeight: 1,
    zIndex: 3,
};

const isTouchDevice = () => {
    return (
        typeof window !== 'undefined' &&
        ('ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0))
    );
};
const setTouchAction = (map, value) => {
    const el = map?.getDiv?.();
    if (!el) return;
    el.style.touchAction = value;
};

const setMapGestureHandling = (map, value) => {
    if (!map?.setOptions) return;
    map.setOptions({ gestureHandling: value });
};

export default function TerraDrawManager({
    map,
    enabled,
    mode,
    onReady,
    onFinish,
    onModeChange,
}) {
    const drawRef = useRef(null);
    const previewOverlayRef = useRef(null);

    const firstDrawSeenAtRef = useRef(0);

    const clearPreview = () => {
        if (previewOverlayRef.current) {
            previewOverlayRef.current.setMap(null);
            previewOverlayRef.current = null;
        }
    };

    useEffect(() => {
        if (!map) return;
        if (!isTouchDevice()) return;

        if (enabled && mode === MODES.RECTANGLE) {
            setTouchAction(map, 'none');
            setMapGestureHandling(map, 'none');
        } else {
            setTouchAction(map, 'auto');
            setMapGestureHandling(map, 'greedy');
        }

        return () => {
            setTouchAction(map, 'auto');
            setMapGestureHandling(map, 'greedy');
        };
    }, [map, mode, enabled]);

    useEffect(() => {
        if (!map) return;
        if (!window?.google?.maps) return;

        if (!enabled) {
            drawRef.current?.stop?.();
            drawRef.current = null;
            clearPreview();
            firstDrawSeenAtRef.current = 0;
            onReady?.(null);
            return;
        }

        const draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({
                map,
                lib: window.google.maps,
                coordinatePrecision: 9,
            }),
            modes: [
                new TerraDrawSelectMode({}),
                new TerraDrawRectangleMode({
                    drawInteraction: 'click-move-or-drag',
                    styles: {
                        fillColor: RECTANGLE_STYLES.fillColor,
                        fillOpacity: RECTANGLE_STYLES.fillOpacity,
                        outlineColor: RECTANGLE_STYLES.outlineColor,
                        outlineWidth: RECTANGLE_STYLES.strokeWeight,
                    },
                }),
            ],
        });

        drawRef.current = draw;

        const handleReady = () => {
            onReady?.(draw);
            draw.setMode(MODES.SELECT);
            onModeChange?.(MODES.SELECT);
        };

        const handleChange = () => {
                const snapshot = draw.getSnapshot();
                let drawingFeature = null;
                for (let i = snapshot.length - 1; i >= 0; i -= 1) {
                    const f = snapshot[i];
                    if (f?.geometry?.type === 'Polygon') {
                        drawingFeature = f;
                        break;
                    }
                }

                if (!drawingFeature) return;

                if (!firstDrawSeenAtRef.current) {
                    firstDrawSeenAtRef.current = Date.now();
                }

                const coordinates = drawingFeature.geometry.coordinates?.[0];
                if (!Array.isArray(coordinates) || coordinates.length === 0) return;

                const paths = coordinates.map(([lng, lat]) => ({ lat, lng }));

                if (previewOverlayRef.current) {
                    previewOverlayRef.current.setPaths(paths);
                } else {
                    previewOverlayRef.current = new window.google.maps.Polygon({
                        paths,
                        ...RECTANGLE_STYLES,
                        clickable: false,
                        draggable: false,
                        map,
                    });
                }
        };

        const handleFinish = (ids, type) => {
                const snapshot = draw.getSnapshot();
                const idArr = Array.isArray(ids) ? ids : ids ? [ids] : [];
                const features = idArr.length ? snapshot.filter((f) => idArr.includes(f.id)) : snapshot;

                const startedAt = firstDrawSeenAtRef.current;
                firstDrawSeenAtRef.current = 0;
                const elapsed = startedAt ? Date.now() - startedAt : 9999;

                if (elapsed < 120) {
                    if (idArr.length > 0) {
                        try {
                            draw.removeFeatures(idArr);
                        } catch (error) {
                            console.warn(error);
                        }
                    }
                    clearPreview();
                    draw.setMode(MODES.SELECT);
                    onModeChange?.(MODES.SELECT);
                    return;
                }

                if (idArr.length > 0) {
                    try {
                        draw.removeFeatures(idArr);
                    } catch (error) {
                        console.warn(error);
                    }
                }

                clearPreview();

                onFinish?.(draw, { ids: idArr, type, features, snapshot });

                draw.setMode(MODES.SELECT);
                onModeChange?.(MODES.SELECT);
        };

        draw.on('ready', handleReady);
        draw.on('change', handleChange);
        draw.on('finish', handleFinish);

        let started = false;
        const tryStart = () => {
            if (started) return true;
            if (!enabled) return false;

            const hasDiv = !!map?.getDiv?.();
            const hasBounds = !!map?.getBounds?.();
            if (!hasDiv || !hasBounds) return false;

            started = true;
            draw.start();
            return true;
        };

        const idleListener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
            if (tryStart()) {
                clearInterval(pollTimer);
            }
        });

        const pollTimer = setInterval(() => {
            if (tryStart()) {
                clearInterval(pollTimer);
                window.google.maps.event.removeListener(idleListener);
            }
        }, 50);

        return () => {
            clearInterval(pollTimer);
            window.google.maps.event.removeListener(idleListener);
            try {
                draw.off('ready', handleReady);
                draw.off('change', handleChange);
                draw.off('finish', handleFinish);
            } catch (error) {
                console.warn(error);
            }
            draw.stop?.();
            drawRef.current = null;
            clearPreview();
            firstDrawSeenAtRef.current = 0;
            onReady?.(null);
        };
    }, [map, enabled, onReady, onFinish, onModeChange]);

    return null;
}

TerraDrawManager.propTypes = {
    map: PropTypes.object,
    enabled: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf([MODES.SELECT, MODES.RECTANGLE]),
    onReady: PropTypes.func,
    onFinish: PropTypes.func,
    onModeChange: PropTypes.func,
};

TerraDrawManager.defaultProps = {
    mode: MODES.SELECT,
    onReady: undefined,
    onFinish: undefined,
    onModeChange: undefined,
};
